/**
 * Shop3 상점 유형별 메인 색상 정의
 *
 * 도매(Supplier/Wholesale): #00ccbf (청록색)
 * 소매(Reseller/Retail): #FF4B6B (핑크/코랄)
 *
 * 사용 예시:
 * import { SHOP_COLORS, getShopColors } from '@/constants/shopColors';
 *
 * // 직접 사용
 * const color = SHOP_COLORS.supplier.main;
 *
 * // 역할 기반 사용
 * const colors = getShopColors('supplier');
 */

// 상점 유형
export type ShopType = 'supplier' | 'reseller';

// 색상 팔레트 인터페이스
export interface ShopColorPalette {
    main: string; // 메인 색상
    light: string; // 밝은 변형
    dark: string; // 어두운 변형
    contrastText: string; // 대비 텍스트 색상
    background: string; // 배경색 (연한)
}

// 도매 쇼핑몰 색상 (Supplier/Wholesale)
export const SUPPLIER_COLORS: ShopColorPalette = {
    main: '#00ccbf', // 청록색
    light: '#4dd9cf',
    dark: '#00b3a6',
    contrastText: '#ffffff',
    background: '#e6faf8',
};

// 소매 쇼핑몰 색상 (Reseller/Retail)
export const RESELLER_COLORS: ShopColorPalette = {
    main: '#FF4B6B', // 핑크/코랄
    light: '#FF7A8F',
    dark: '#E63D5A',
    contrastText: '#ffffff',
    background: '#FFF0F3',
};

// 통합 색상 객체
export const SHOP_COLORS = {
    supplier: SUPPLIER_COLORS,
    reseller: RESELLER_COLORS,
} as const;

// 역할 기반 색상 반환 함수
export const getShopColors = (shopType: ShopType): ShopColorPalette => {
    return SHOP_COLORS[shopType];
};

// MUI 테마 팔레트용 색상 객체 생성
export const getMuiPaletteColors = (shopType: ShopType) => {
    const colors = getShopColors(shopType);
    return {
        primary: {
            main: colors.main,
            light: colors.light,
            dark: colors.dark,
            contrastText: colors.contrastText,
        },
    };
};

// MUI sx prop에서 사용할 스타일 객체 생성
export const getInputStyles = (shopType: ShopType) => {
    const colors = getShopColors(shopType);
    return {
        '& .MuiOutlinedInput-root': {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.main,
            },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: colors.main,
        },
    };
};

export const getRadioStyles = (shopType: ShopType) => {
    const colors = getShopColors(shopType);
    return {
        '&.Mui-checked': {
            color: colors.main,
        },
    };
};

export const getCheckboxStyles = (shopType: ShopType) => {
    const colors = getShopColors(shopType);
    return {
        '&.Mui-checked': {
            color: colors.main,
        },
    };
};
